import { useState, useCallback } from "react";

interface RequestOptions {
    method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
    body?: Record<string, any> | null;
}

interface WekaApiHook {
    get: <T = any>(endpoint: string, params?: Record<string, any>) => Promise<T>;
    post: <T = any>(endpoint: string, body: Record<string, any>) => Promise<T>;
    del: <T = any>(endpoint: string, body: Record<string, any>) => Promise<T>;
    loading: boolean;
    error: Error | null;
}

export interface GenericResponse {
    success: boolean,
    message: string
}

export interface GenericErrorResponse {
    code: string;
    message: string;
    data: {
        status: number;
    };
}

export function useApi(): WekaApiHook {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const request = useCallback(
        async <T = any>(endpoint: string, { method = "GET", body = null }: RequestOptions = {}): Promise<T> => {
            setLoading(true);
            setError(null);

            try {
                const headers: Record<string, string> = {
                    Accept: "application/json",
                };

                if (body) {
                    headers["Content-Type"] = "application/json";
                }

                if (wekaApiSettings.nonce) {
                    headers["X-WP-Nonce"] = wekaApiSettings.nonce;
                }

                const res = await fetch('/wp-json/' + wekaApiSettings.api_endpoint_base + endpoint, {
                    method,
                    credentials: 'same-origin',
                    headers,
                    body: body ? JSON.stringify(body) : undefined,
                });

                if (!res.ok) {
                    throw new Error(await res.text());
                }

                return (await res.json()) as T;
            } catch (err) {
                const errorObj = err instanceof Error ? err : new Error(String(err));
                setError(errorObj);
                throw errorObj;
            } finally {
                setLoading(false);
            }
        },
        []
    );

    const get = useCallback(
        <T = any>(endpoint: string, params?: Record<string, any>) => {
            let url = endpoint;
            if (params) {
                const query = new URLSearchParams(params as Record<string, string>).toString();
                url += `?${query}`;
            }
            return request<T>(url, { method: "GET" });
        },
        [request]
    );

    const post = useCallback(
        <T = any>(endpoint: string, body: Record<string, any>) => request<T>(endpoint, { method: "POST", body }),
        [request]
    );

    const del = useCallback(
        <T = any>(endpoint: string, body: Record<string, any>) => request<T>(endpoint, { method: "DELETE", body }),
        [request]
    );

    return { get, post, del, loading, error };
}
